<template>
  <section>
    <div class="flex items-center gap-2 font-bold">
      <v-icon v-bind="statusIcon" size="small" />
      {{ t("swanKyb.verification.title") }}
    </div>
    <div class="text-sm mt-1">
      {{
        t(`swanKyb.verification.${kyb.verification.status}`, {
          date: kyb.verification.since
            ? d(kyb.verification.since.toDate(), { dateStyle: "long" })
            : "",
          reason: kyb.verification.refusalReason ?? "",
        })
      }}
    </div>
    <div v-if="kyb.verification.requirements.length" class="text-sm mt-2">
      {{ t("swanKyb.verification.requirements") }}
      <ul class="list-disc ml-6">
        <li v-for="requirement in kyb.verification.requirements" :key="requirement">
          {{ t(`swanKyb.requirements.${requirement}`) }}
        </li>
      </ul>
    </div>
    <ul v-if="documents.length" class="mt-3 flex list-none flex-col items-start gap-2 p-0">
      <li
        v-for="document in documents"
        :key="document.swanPurpose"
        v-tooltip="
          document.status === 'refused'
            ? `${document.swanPurpose} — ${document.swanReasonCode}`
            : document.swanPurpose
        "
        class="rounded-lg border border-solid border-gray-200 px-3 py-2 dark:border-white/10"
      >
        <div class="flex items-center gap-3">
          <v-icon class="text-medium-emphasis" icon="mdi-file-document-outline" size="small" />
          <span class="text-sm">{{ t(`swanKyb.documents.${document.purpose}`) }}</span>
          <v-chip size="small" variant="tonal" :color="documentStatusColor[document.status]">
            {{
              document.status === "refused"
                ? t("swanKyb.documentStatus.refused", {
                    reason: t(`swanKyb.refusalReasons.${document.reason}`),
                  })
                : t(`swanKyb.documentStatus.${document.status}`)
            }}
          </v-chip>
        </div>
        <template v-if="document.status === 'missing' || document.status === 'refused'">
          <div v-if="document.purposeDetails" class="text-sm mt-1 ml-8">
            {{ document.purposeDetails }}
          </div>
          <div v-if="document.acceptedTypes" class="text-xs text-medium-emphasis mt-1 ml-8">
            {{ t("swanKyb.acceptedTypes", { types: document.acceptedTypes }) }}
          </div>
        </template>
      </li>
    </ul>
    <v-btn
      v-if="kyb.collectionUrl"
      class="mt-2 px-0"
      variant="plain"
      :href="kyb.collectionUrl"
      target="_blank"
      rel="noopener"
      append-icon="mdi-open-in-new"
      >{{ t("swanKyb.verification.openInSwan") }}</v-btn
    >
  </section>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import {
  swanKybDocumentTypes,
  type SwanKyb,
  type SwanKybDocumentStatus,
} from "@aircap/common/swan";
import { computed } from "vue";

const props = defineProps<{
  kyb: SwanKyb;
}>();

const { t, d } = useI18n();

const statusIcon = computed(() => {
  switch (props.kyb.verification.status) {
    case "NotStarted":
    case "WaitingForInformation":
    case "Pending":
      return { icon: "mdi-progress-clock", color: "#9E9E9E" };
    case "Refused":
      return { icon: "mdi-alert-circle", color: "error" };
    case "Verified":
      return { icon: "mdi-check-circle", color: "success" };
  }
});

const documents = computed(() =>
  props.kyb.documents.map((document) => ({
    ...document,
    acceptedTypes: (document.acceptableTypes ?? [])
      .map((type) =>
        t(
          `swanKyb.documentTypes.${swanKybDocumentTypes.find((known) => known === type) ?? "Other"}`,
        ),
      )
      .join(", "),
  })),
);

const documentStatusColor: Record<SwanKybDocumentStatus, string> = {
  missing: "warning",
  inReview: "grey",
  validated: "success",
  refused: "error",
};
</script>
